<template>
  <form class="custom-form" @submit.prevent="save" novalidate>
    <section class="form-section">
      <h2 class="form-subtitle">Datos del proveedor</h2>

      <div class="row">
        <div class="col-md-6">
          <div class="form-group">
            <label>Tipo de proveedor *</label>
            <div class="type-radios">
              <label class="radio-option">
                <input type="radio" value="ASOCIADO" v-model="form.provider_type" :disabled="editing" />
                <span>Asociado</span>
              </label>
              <label class="radio-option">
                <input type="radio" value="TRANSPORTISTA" v-model="form.provider_type" :disabled="editing" />
                <span>Transportista</span>
              </label>
            </div>
            <small v-if="editing" class="text-muted">El tipo no se puede cambiar.</small>
            <small v-else-if="form.provider_type === 'ASOCIADO'" class="text-muted">
              Se creará también su conductor con acceso a la app (contraseña: su celular).
            </small>
            <FormError
              v-if="$v.form.provider_type.$error && !$v.form.provider_type.required"
              message="Tipo de proveedor es requerido"
            />
          </div>
        </div>

        <div class="col-md-6">
          <div class="form-group">
            <label for="city">Ciudad</label>
            <input id="city" type="text" class="custom-form-control" :value="cityLabel" disabled />
            <small class="text-muted">Es la ciudad activa del portal.</small>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <div class="form-group">
            <label for="contact_name">Nombre de contacto *</label>
            <input
              id="contact_name"
              type="text"
              v-model="form.contact_name"
              autocomplete="off"
              placeholder="Nombre de la persona de contacto"
              maxlength="200"
              class="custom-form-control"
              :class="{ 'custom-form-control-error': $v.form.contact_name.$error }"
            />
            <FormError
              v-if="$v.form.contact_name.$error && !$v.form.contact_name.required"
              message="Nombre de contacto es requerido"
            />
          </div>
        </div>

        <div class="col-md-6">
          <div class="form-group">
            <label for="business_name">Nombre comercial</label>
            <input
              id="business_name"
              type="text"
              v-model="form.business_name"
              autocomplete="off"
              placeholder="Razón social o nombre comercial"
              maxlength="200"
              class="custom-form-control"
            />
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-4">
          <div class="form-group">
            <label for="dni">DNI</label>
            <input id="dni" type="text" v-model="form.dni" maxlength="15" class="custom-form-control"
              placeholder="Número de documento" />
          </div>
        </div>
        <div class="col-md-4">
          <div class="form-group">
            <label for="phone">Teléfono *</label>
            <input
              id="phone"
              type="text"
              v-model="form.phone"
              maxlength="20"
              class="custom-form-control"
              :class="{ 'custom-form-control-error': $v.form.phone.$error }"
              placeholder="Celular principal"
            />
            <FormError
              v-if="$v.form.phone.$error && !$v.form.phone.required"
              message="Teléfono es requerido"
            />
          </div>
        </div>
        <div class="col-md-4">
          <div class="form-group">
            <label for="phone_secondary">Teléfono alterno</label>
            <input id="phone_secondary" type="text" v-model="form.phone_secondary" maxlength="20"
              class="custom-form-control" placeholder="Opcional" />
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <div class="form-group">
            <label for="email">Correo electrónico</label>
            <input id="email" type="email" v-model="form.email" maxlength="100" class="custom-form-control"
              placeholder="correo@dominio.com" />
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label for="address">Dirección</label>
            <input id="address" type="text" v-model="form.address" maxlength="300" class="custom-form-control"
              placeholder="Dirección" />
          </div>
        </div>
      </div>

      <h2 class="form-subtitle mt-3">Datos bancarios</h2>
      <div class="row">
        <div class="col-md-4">
          <div class="form-group">
            <label for="bank_name">Banco</label>
            <input id="bank_name" type="text" v-model="form.bank_name" maxlength="100" class="custom-form-control"
              placeholder="Nombre del banco" />
          </div>
        </div>
        <div class="col-md-4">
          <div class="form-group">
            <label for="bank_account_number">Número de cuenta</label>
            <input id="bank_account_number" type="text" v-model="form.bank_account_number" maxlength="50"
              class="custom-form-control" placeholder="Número de cuenta" />
          </div>
        </div>
        <div class="col-md-4">
          <div class="form-group">
            <label for="bank_cci">CCI</label>
            <input id="bank_cci" type="text" v-model="form.bank_cci" maxlength="30" class="custom-form-control"
              placeholder="Código interbancario" />
          </div>
        </div>
      </div>

      <h2 class="form-subtitle mt-3">Pagos</h2>
      <div class="row">
        <div class="col-md-4">
          <div class="form-group">
            <label for="payment_frequency">Frecuencia de pago *</label>
            <FormSelect
              id="payment_frequency"
              :items="paymentFrequencies"
              v-model="form.payment_frequency"
              defaultOption="Elegir frecuencia"
              :showError="$v.form.payment_frequency.$error"
            />
            <FormError
              v-if="$v.form.payment_frequency.$error && !$v.form.payment_frequency.required"
              message="Frecuencia de pago es requerida"
            />
          </div>
        </div>
        <div class="col-md-8">
          <div class="form-group">
            <label for="notes">Notas</label>
            <textarea id="notes" v-model="form.notes" rows="2" class="custom-form-control"
              placeholder="Observaciones internas"></textarea>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <div class="form-group-buttons text-right">
            <router-link :to="{ name: 'providers-list' }" class="button button-light">
              {{ editing ? "Volver" : "Cancelar" }}
            </router-link>
            <button class="button button-primary" type="submit">Guardar</button>
          </div>
        </div>
      </div>
    </section>
  </form>
</template>

<script>
import { Alert, FormSelect, FormError } from "wize-admin";
import { required } from "vuelidate/lib/validators";

import { ProviderService } from "@/core/services";
import { Constants as LocalConstants } from "@/core/utils";

// Formulario de proveedor (DEF-ADM-018). Sirve para crear y para la tab General
// del detalle. La ciudad no se edita: sale del header X-City-Id.
export default {
  name: "ProviderForm",
  components: { FormSelect, FormError },
  props: {
    provider: { type: Object, default: null },
  },
  data() {
    return {
      form: {
        provider_type: null,
        contact_name: null,
        business_name: null,
        dni: null,
        phone: null,
        phone_secondary: null,
        email: null,
        address: null,
        bank_name: null,
        bank_account_number: null,
        bank_cci: null,
        payment_frequency: "WEEKLY",
        notes: null,
      },
      paymentFrequencies: Object.values(LocalConstants.PaymentFrequency),
    };
  },
  validations: {
    form: {
      provider_type: { required },
      contact_name: { required },
      phone: { required },
      payment_frequency: { required },
    },
  },
  computed: {
    editing() {
      return !!(this.provider && this.provider.id);
    },
    cityLabel() {
      if (this.provider && this.provider.city_name) return this.provider.city_name;
      const city = this.$store.getters["city/getCurrentCity"];
      return city ? city.name : "Ciudad activa";
    },
  },
  watch: {
    provider: {
      immediate: true,
      handler(val) {
        if (val) this.form = { ...this.form, ...val };
      },
    },
  },
  methods: {
    async save() {
      this.$v.$touch();
      if (this.$v.$error) return;

      try {
        await this.$store.dispatch("app/loading", true);
        const data = { ...this.form };

        if (this.editing) {
          await ProviderService.update(this.provider.id, data);
          Alert.success("Se almacenó la información correctamente");
          this.$emit("saved");
        } else {
          const res = await ProviderService.save(data);
          Alert.success("Proveedor creado correctamente");
          this.$router.push({
            name: "providers-detail",
            params: { id: res.payload.id },
          });
        }
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "No se pudo guardar el proveedor");
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
  },
};
</script>

<style lang="stylus" scoped>
.type-radios
  display flex
  gap 24px
  align-items center
  height 42px
.radio-option
  display flex
  align-items center
  gap 6px
  margin 0
  cursor pointer
  font-weight 500
  input
    cursor pointer
</style>
